import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import GTA    from '../../assets/partners/gta.jpg';
import GHATOF from '../../assets/partners/ghatof.jpg';
import TOSOGHA from '../../assets/partners/tosogha.png';
import GTDC   from '../../assets/partners/gtdc.jpg';
import HOTCATT from '../../assets/partners/HOTCATT.png';
import MOT    from '../../assets/partners/mot.jpg';
import CAA    from '../../assets/partners/caa.jpeg';
import AHTEC  from '../../assets/partners/AHTEC.jpeg';
import AKUNA  from '../../assets/partners/AKUNA.png';
import UGBS   from '../../assets/partners/ugbs.png';
import HeroCarousel from '../sections/Herocarousel';
import { publicService } from '../../api/services';

const VIDEO_URL = 'https://firebasestorage.googleapis.com/v0/b/images-1aaab.appspot.com/o/20546634-uhd_3840_2160_30fps.mp4?alt=media&token=43e2bb92-2523-416f-a0b3-a1c42a351537';

const partners = [
  { name: 'GTDC',    src: GTDC },
  { name: 'GTA',     src: GTA },
  { name: 'MINISTRY OF TOURISM',  src: MOT },
  { name: 'HOTCATT', src: HOTCATT },
  { name: 'CREATIVE ARTS AGENCY', src: CAA },
  { name: 'GHATOF',  src: GHATOF },
  { name: 'TOSOGHA', src: TOSOGHA },
  { name: 'AHTEC', src: AHTEC },
  { name: 'The Akuna Group', src: AKUNA },
  { name: 'UGBS', src: UGBS },
];

// ── Animated counter hook ─────────────────────────────────────
const useCounter = (target, duration = 1800, delay = 500) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!target) return;
    const start = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - start) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(e * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    const t = setTimeout(() => requestAnimationFrame(tick), delay);
    return () => clearTimeout(t);
  }, [target, duration, delay]);
  return value;
};

const Hero = () => {
  const [loaded,       setLoaded]       = useState(false);
  const [videoReady,   setVideoReady]   = useState(false);
  const [platformStats, setPlatformStats] = useState(null);
  const videoRef = React.useRef(null);

  // Load platform stats from backend
  useEffect(() => {
    publicService.getStats()
      .then(res => {
        const d = res.data?.data ?? res.data;
        setPlatformStats(d);
      })
      .catch(() => {}); // fail silently — keep zeros
  }, []);

  // Android fix: force play on mount + 4s fallback
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
    const t = setTimeout(() => setVideoReady(true), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
  }, []);

  // Animate each stat when backend data arrives
  const expertCount  = useCounter(platformStats?.experts  ?? 0);
  const sessionCount = useCounter(platformStats?.sessions ?? 0);
  const clientCount  = useCounter(platformStats?.clients  ?? 0);

  // console.log('Platform stats:', platformStats);

  const stats = [
    {
      value: platformStats ? `${expertCount}+`  : '—',
      label: 'Experts',
    },
    // {
    //   value: platformStats?.avgRating ? `${platformStats.avgRating}★` : '—',
    //   label: 'Avg Rating',
    // },
    {
      value: platformStats ? `${sessionCount.toLocaleString()}+` : '—',
      label: 'Sessions',
    },
    {
      value: platformStats ? `${clientCount.toLocaleString()}+`  : '—',
      label: 'Clients',
    },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;1,400&family=Outfit:wght@400;500;600&display=swap');
        .hero-font-serif { font-family: 'Playfair Display', Georgia, serif; }
        .hero-font-sans  { font-family: 'Outfit', system-ui, sans-serif; }

        @keyframes heroPulse  { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes scrollFade { 0%,100%{opacity:0.4;transform:translateY(0)} 50%{opacity:0.1;transform:translateY(8px)} }
        @keyframes spinRing   { to { transform: rotate(360deg); } }
        @keyframes textPulse  { 0%,100%{opacity:0.5} 50%{opacity:1} }
        @keyframes kenteMove  {
          from { background-position: 0 0; }
          to   { background-position: 128px 0; }
        }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes partnerScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .hero-pulse  { animation: heroPulse  2s infinite; }
        .scroll-fade { animation: scrollFade 2s infinite; }
        .spin-ring   { animation: spinRing   1s linear infinite; }
        .text-pulse  { animation: textPulse  2s ease infinite; }
        .kente-move  {
          background: repeating-linear-gradient(90deg,
            #dc2626 0, #dc2626 16px, #d97706 16px, #d97706 32px,
            #16a34a 32px, #16a34a 48px, #1d4ed8 48px, #1d4ed8 64px,
            #fbbf24 64px, #fbbf24 80px, #dc2626 80px, #dc2626 96px,
            #16a34a 96px, #16a34a 112px, #d97706 112px, #d97706 128px
          );
          background-size: 128px 100%;
          animation: kenteMove 1s linear infinite;
        }
        .shimmer-bar {
          background: linear-gradient(90deg,
            rgba(255,255,255,0.04) 0%,
            rgba(255,255,255,0.12) 40%,
            rgba(255,255,255,0.04) 80%
          );
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite linear;
        }
        .partner-track {
          display: flex;
          width: max-content;
          animation: partnerScroll 30s linear infinite;
        }
        .partner-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      <section className="relative hero-font-sans">

        <div className="relative flex flex-col overflow-hidden">

          {/* Loading screen */}
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center z-20 transition-opacity duration-1000
              ${videoReady ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            style={{ background: '#020a05' }}
          >
            <div className="absolute top-0 left-0 right-0 h-1 kente-move" />
            <div className="flex flex-col items-center gap-7 text-center px-8">
              <div className="relative w-14 h-14">
                <div className="absolute inset-0 rounded-full border-2 border-white/10" />
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-green-400 spin-ring" />
                <div className="absolute inset-0 flex items-center justify-center text-lg">🇬🇭</div>
              </div>
              <div>
                <p className="hero-font-serif text-white/80 text-2xl font-light italic text-pulse">TourEnt Hub</p>
                <p className="text-white/25 text-[11px] uppercase tracking-[0.18em] mt-1.5">Entrepreneurship Capacity Building Platform</p>
              </div>
              <div className="w-56 space-y-2.5">
                <div className="h-1.5 rounded-full shimmer-bar" />
                <div className="h-1.5 rounded-full shimmer-bar" style={{ width: '75%', margin: '0 auto' }} />
                <div className="h-1.5 rounded-full shimmer-bar" style={{ width: '50%', margin: '0 auto' }} />
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 kente-move" />
          </div>

          {/* Video */}
          <video
            ref={videoRef}
            autoPlay muted loop playsInline
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ zIndex: 0, opacity: videoReady ? 1 : 0 }}
            onCanPlay={() => setVideoReady(true)}
            onLoadedData={() => setVideoReady(true)}
            onError={() => setVideoReady(true)}
            preload="auto"
          >
            <source src={VIDEO_URL} type="video/mp4" />
          </video>

          {/* Overlay */}
          <div
            className="absolute inset-0"
            style={{
              zIndex: 1,
              background: 'linear-gradient(180deg, rgba(2,10,5,0.52) 0%, rgba(2,10,5,0.52) 55%, rgba(1,6,3,0.88) 78%, rgba(0,3,1,0.97) 100%)',
            }}
          />

          {/* Kente stripe */}
          <div
            className="absolute top-0 left-0 right-0 h-[3px]"
            style={{ zIndex: 2, background: 'linear-gradient(90deg, #dc2626, #d97706, #16a34a, #d97706, #dc2626)' }}
          />

          {/* Carousel — desktop top-right */}
          <div className="absolute top-24 right-6 z-20 hidden lg:block">
            <HeroCarousel />
          </div>

          {/* Hero content */}
          <div className={`relative z-10 max-w-3xl mx-auto px-6 sm:px-8 text-center pt-36 pb-16 transition-all duration-700
            ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>

            {/* Headline */}
            <h1 className="mb-2 leading-[1.06]">
              <span className="block hero-font-serif text-3xl sm:text-4xl lg:text-4xl font-bold text-white tracking-tight drop-shadow-lg">
                Develop your
              </span>
              <span className="block hero-font-serif text-3xl sm:text-4xl lg:text-5xl font-light italic tracking-tight text-green-300 drop-shadow-md">
                Entrepreneurship Capacity.
              </span>
            </h1>

            {/* Sub-copy */}
            <p className="text-base sm:text-lg text-white/60 leading-relaxed max-w-xl mx-auto mb-12">
              Connect with top Experts through live video, audio or chat sessions.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-16">
              <a href="/experts"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-white text-sm
                  bg-gradient-to-br from-green-700 to-green-500 hover:scale-[1.03] active:scale-[0.98]
                  transition-transform shadow-lg shadow-green-900/40">
                Find an Expert <ArrowRight size={15} />
              </a>
              <a href="/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-medium text-white/85 text-sm
                  bg-white/10 border border-white/20 backdrop-blur-sm hover:bg-white/[0.17] hover:scale-[1.03] transition-all">
                Join as Expert
              </a>
            </div>

            {/* Stats — live from backend with animated count-up */}
            <div className="grid grid-cols-3 border-t border-white/[0.12] pt-2">
              {stats.map((s, i) => (
                <div key={s.label} className={`text-center px-2 ${i < 3 ? 'border-r border-white/[0.12]' : ''}`}>
                  <div className="hero-font-serif text-2xl font-bold text-green-300">
                    {s.value}
                  </div>
                  <div className="text-[10px] text-white/35 mt-1 uppercase tracking-widest font-semibold">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Carousel — mobile below hero text */}
          <div className="lg:hidden relative z-10 px-4 pb-8 flex justify-center">
            <HeroCarousel />
          </div>

          {/* Partners */}
          <div className="relative z-10 px-6 pt-8 pb-10">
            <div className="max-w-5xl mx-auto mb-2 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
            <p className="text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white mb-5">
              Our Partners
            </p>
            <div
              className="overflow-hidden"
              style={{
                maskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
              }}
            >
              <div className="partner-track">
                {[...partners, ...partners].map((p, i) => (
                  <div
                    key={`${p.name}-${i}`}
                    className="flex flex-col items-center gap-3 cursor-default flex-shrink-0 mx-10"
                  >
                    <img
                      src={p.src}
                      alt={p.name}
                      className="h-14 w-auto object-contain rounded-lg"
                      style={{ opacity: 0.85 }}
                    />
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-white/25 hover:text-white/60 transition-colors duration-300 whitespace-nowrap">
                      {p.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default Hero;